import React, { useState, useEffect, useCallback } from "react"
import {
  Typography,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  InputLabel,
  FormControl,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { useAuthUser } from "react-auth-kit"
import api from "../../../../utils/api"

interface InstanceData {
  id: number
  description: string
  eventId: any
  status: string
}

interface EventData {
  instanceNum: number
}

type NotificationSnackbarProps = {
  message: string | null
  onClose: () => void
}

const NotificationSnackbar: React.FC<NotificationSnackbarProps> = ({
  message,
  onClose,
}) => (
  <Snackbar
    open={!!message}
    autoHideDuration={3000}
    onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
  >
    <Alert onClose={onClose} severity="success" sx={{ width: "100%" }}>
      {message}
    </Alert>
  </Snackbar>
)

type DeleteInstancePageInnerProps = {
  instances: InstanceData[]
  onInstanceSelect: (instanceId: any) => void
  onDelete: () => void
  selectedInstanceId: string
}

const DeleteInstancePageInner: React.FC<DeleteInstancePageInnerProps> = ({
  instances,
  onInstanceSelect,
  onDelete,
  selectedInstanceId,
}) => (
  <Container component="main">
    <Typography variant="h4" gutterBottom>
      Delete Instance
    </Typography>
    <form onSubmit={(e) => e.preventDefault()}>
      <FormControl fullWidth margin="normal" required>
        {instances.length > 0 && (
          <InputLabel id="instance-select-label">Select an Instance</InputLabel>
        )}
        {instances.length > 0 ? (
          <Select
            label="Select a Instance"
            labelId="instance-select-label"
            value={selectedInstanceId}
            onChange={(e) => onInstanceSelect(e.target.value)}
          >
            {instances.some((instance) => instance.status === "pre-voting") ? (
              instances.map(
                (instance) =>
                  instance.status === "pre-voting" && (
                    <MenuItem key={instance.id} value={instance.id}>
                      {instance.description}
                    </MenuItem>
                  )
              )
            ) : (
              <MenuItem disabled>No instances available</MenuItem>
            )}
          </Select>
        ) : (
          <Typography variant="body1">No instances available</Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={onDelete}
        >
          Delete
        </Button>
      </FormControl>
    </form>
  </Container>
)

type Props = {
  notification?: string
}

const DeleteInstancePage: React.FC<Props> = ({ notification = "" }) => {
  const auth = useAuthUser()
  const navigate = useNavigate()
  const [selectedInstanceId, setSelectedInstanceId] = useState("")
  const [instances, setInstances] = useState<InstanceData[]>([])
  const [instanceNum, setInstanceNum] = useState(0)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isDeleteAllConfirmationOpen, setIsDeleteAllConfirmationOpen] =
    useState(false)
  const [deletingAllInstances, setDeletingAllInstances] =
    useState<boolean>(false)

  //Testing
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const handleDelete = () => {
    setIsDeleteConfirmationOpen(true)
  }
  const handleConfirmDelete = async () => {
    setIsDeleteConfirmationOpen(false)

    if (!selectedInstanceId) {
      return
    }

    try {
      await api.delete(`/admin/instances/${selectedInstanceId}`)
      navigate("/admin/dashboard", {
        state: { notification: "Instance successfully deleted!" },
      })
    } catch (error) {
      console.error("Error deleting instance:", error)
    }
  }
  const handleCancelDelete = () => {
    setIsDeleteConfirmationOpen(false)
  }

  const fetchInstances = useCallback(async (eventId: string) => {
    try {
      const response = await api.get(
        `/admin/events/${eventId}/voting-instances`
      )

      setInstances(response.data)
    } catch (err) {
      console.error("Error fetching voting instances:", err)
    }
  }, [])

  const handleSnackbarClose = () => {
    setOpenSnackbar(false)
    setErrorMessage("")
  }

  const handleInstanceSelect = (instanceId: any) => {
    setSelectedInstanceId(instanceId)
  }

  const handleDeleteAll = async () => {
    setDeletingAllInstances(true)

    for (let instance of instances) {
      if (instance.status === "pre-voting") {
        try {
          await api.delete(`/admin/instances/${instance.id}`)
        } catch (error) {
          console.error(
            `Error deleting instance with ID: ${instance.id}:`,
            error
          )
        }
      }
    }
    setInstances((prevInstances) =>
      prevInstances.filter(
        (instance) => !instances.some((i) => i.id === instance.id)
      )
    )

    setIsDeleteAllConfirmationOpen(false)
    setDeletingAllInstances(false)
  }

  const eventRef = auth()?.eventId ?? ""
  const eventId = eventRef._path?.segments[1] ?? ""

  useEffect(() => {
    fetchInstances(eventId)
  }, [fetchInstances, eventId])

  return (
    <Container>
      <DeleteInstancePageInner
        instances={instances}
        onInstanceSelect={handleInstanceSelect}
        onDelete={handleDelete}
        selectedInstanceId={selectedInstanceId}
      />
      {/* {instances.length > 0 && ( */}
      {instances.some((instance) => instance.status === "pre-voting") ? (
        instances.length > 0 && (
          <Button
            variant="contained"
            style={{ backgroundColor: "red" }}
            aria-label="Delete All"
            sx={{ marginRight: 1 }}
            onClick={() => {
              setIsDeleteAllConfirmationOpen(true)
            }}
          >
            Delete All
          </Button>
        )
      ) : (
        <Typography variant="body1">No instances available</Typography>
      )}

      {/*Start of Delete All Dialog */}
      <Dialog
        open={isDeleteAllConfirmationOpen}
        onClose={() => {
          setIsDeleteAllConfirmationOpen(false)
          setDeletingAllInstances(false) // Reset the state when closing the dialog
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deletingAllInstances ? (
            <Typography>
              Please wait, this may take a couple of minutes...
            </Typography>
          ) : (
            <Typography>
              Are you sure you want to delete all instances?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteAllConfirmationOpen(false)
              setDeletingAllInstances(false) // Reset the state when canceling
            }}
            color="primary"
          >
            Cancel
          </Button>
          {!deletingAllInstances && ( // Hide the Confirm button during the deletion process
            <Button
              onClick={async () => {
                setDeletingAllInstances(true)
                await handleDeleteAll()
                setIsDeleteAllConfirmationOpen(false)
                setDeletingAllInstances(false) // Reset the state after deletion
              }}
              color="primary"
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Start of Delete single instance */}
      <Dialog open={isDeleteConfirmationOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this instance?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default DeleteInstancePage
